import { Box, Button, Flex, Image, Skeleton, Stack, Text } from '@chakra-ui/react';
import { CaretLeft, CaretRight } from '@phosphor-icons/react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Calendar from 'react-calendar';
import { Value } from 'react-calendar/dist/esm/shared/types.js';
import { format, isSameDay } from 'date-fns';

import { fetchAPI } from '@/api/fetch';
import { apiGetAccountProfile } from '@/api/services';
import {
  apiGetMeetingDashboard,
  apiGetRecentMeetings,
  GetMeetingDashboardResponse
} from '@/api/services/meeting.service';
import { MAX_MEETING_TIME_TOTAL } from '@/config';
import emptyJobImage from '@/assets/image/empty-job-cover.svg';
import { AgencyPlan, AgencyUsageType } from '@/types/enums';
import { MeetingSummary } from '@/types/Meeting';

import RecentInterviewCard from './RecentInterviewCard';
import UpcomingInterviewCard from './UpcomingInterviewCard';

import 'react-calendar/dist/Calendar.css';
import './InterviewCalendar.css';
import { AgencyUsage } from '@/types/AgencyUsage';

interface DateInterview {
  date: Date;
  meetings: MeetingSummary[];
}

const LoadingSkeleton: React.FC<{ count?: number }> = ({ count = 1 }) => (
  <>
    {Array.from({ length: count }, (_, index) => (
      <Skeleton
        key={index}
        w="100%"
        h="148px"
        border="1px solid"
        borderColor="neutrals.200"
        borderRadius="12px"
      />
    ))}
  </>
);

const EmptyState: React.FC = () => (
  <Stack w="100%" h="100%" align="center" role="region" aria-label="No interviews">
    <Image src={emptyJobImage} h="280px" w="280px" mt="64px" alt="No interviews illustration" />
    <Text fontSize="24px" color="neutrals.800" fontWeight="bold">
      No interviews scheduled
    </Text>
    <Text fontSize="18px" color="neutrals.500" mb="16px">
      Schedule your first interview to get started.
    </Text>
  </Stack>
);

interface NoMeetingsMessageProps {
  title: string;
}

const NoMeetingsMessage: React.FC<NoMeetingsMessageProps> = ({ title }) => (
  <Stack>
    <Text fontSize="24px" color="neutrals.900" fontWeight="bold" mb="24px">
      {title}
    </Text>
    <Text fontSize="18px" color="neutrals.900" fontWeight="bold">
      You don't have any sessions
    </Text>
    <Text fontSize="14px" color="neutrals.700" mb="16px">
      Looks like you don't have any events on your calendar for the selected date. You can create
      new events in your calendar app and then refresh the browser window.
    </Text>
  </Stack>
);

interface UpcomingInterviewsListProps {
  title: string;
  interviews: MeetingSummary[];
  onDeleteSuccess: (meetingId: string) => void;
  onEditSuccess: (meeting: MeetingSummary) => void;
  agencyPlan: AgencyPlan;
}

const UpcomingInterviewsList: React.FC<UpcomingInterviewsListProps> = ({
  title,
  interviews,
  onDeleteSuccess,
  onEditSuccess,
  agencyPlan
}) => (
  <>
    <Text fontSize="24px" color="neutrals.900" fontWeight="bold" mb="24px">
      {title}
    </Text>
    <Stack gap="24px">
      {interviews.map((interview) => (
        <UpcomingInterviewCard
          key={interview.meeting_id}
          meeting={interview}
          onDeleteSuccess={onDeleteSuccess}
          onEditSuccess={onEditSuccess}
          agencyPlan={agencyPlan}
        />
      ))}
    </Stack>
  </>
);

interface CalendarSectionProps {
  upcomingInterviews: DateInterview[];
  onMeetingEdit: (meeting: MeetingSummary) => void;
  onDeleteSuccess: (meetingId: string) => void;
  isLoading: boolean;
  agencyPlan: AgencyPlan;
}

const CalendarSection: React.FC<CalendarSectionProps> = ({
  upcomingInterviews,
  onMeetingEdit,
  onDeleteSuccess,
  isLoading,
  agencyPlan
}) => {
  const [selectedDate, setSelectedDate] = useState<Date>(new Date());
  const [selectedUpcomingInterview, setSelectedUpcomingInterview] = useState<MeetingSummary[]>([]);

  useEffect(() => {
    const today = new Date();
    const nextInterview = upcomingInterviews.find(
      (interview) => isSameDay(interview.date, today) || interview.date > today
    );

    if (nextInterview) {
      setSelectedDate(nextInterview.date);
    }
  }, [upcomingInterviews]);

  useEffect(() => {
    const selectedInterviews =
      upcomingInterviews.find((interview) => isSameDay(interview.date, selectedDate))?.meetings ||
      [];
    setSelectedUpcomingInterview(selectedInterviews);
  }, [selectedDate, upcomingInterviews]);

  const getInterviewDates = () => upcomingInterviews.map((interview) => interview.date);

  const tileContent = ({ date }: { date: Date }) => {
    const hasInterviews = getInterviewDates().some((interviewDate) =>
      isSameDay(interviewDate, date)
    );

    return hasInterviews ? (
      <Box
        position="absolute"
        bottom="5px"
        left="50%"
        transform="translateX(-50%)"
        w="4px"
        h="4px"
        bg="primary.500"
        borderRadius="full"
        role="presentation"
        aria-hidden="true"
      />
    ) : null;
  };

  const handleDateChange = (value: Value) => {
    if (value instanceof Date) {
      setSelectedDate(value);
    }
  };

  const changeMonth = (offset: number) => {
    const newDate = new Date(selectedDate);
    newDate.setMonth(newDate.getMonth() + offset);
    handleDateChange(newDate);
  };

  return (
    <Box
      flex={1}
      h="calc(100vh - 150px)"
      w="100%"
      p="20px 32px 0px 32px"
      bg="background.secondary"
      border="1px solid"
      borderColor="neutrals.200"
      borderRadius="12px"
      boxShadow="sm"
      _hover={{ boxShadow: 'md' }}
      transition="all 0.2s ease-in-out"
    >
      <Flex direction="column" justify="flex-start" align="flex-start" h="100%">
        <Flex justifyContent="space-between" align="center" w="100%">
          <Text fontSize="24px" fontWeight="bold">
            {format(selectedDate, 'MMMM yyyy')}
          </Text>
          <Flex align="center" gap="0">
            <Button
              onClick={() => changeMonth(-1)}
              aria-label="Previous month"
              variant="ghost"
              p="0"
              _hover={{ bg: 'transparent' }}
            >
              <CaretLeft size={32} />
            </Button>
            <Box
              onClick={() => handleDateChange(new Date())}
              aria-label="Today"
              fontSize="12px"
              color="neutrals.700"
              cursor="pointer"
              border="1px solid"
              borderColor="neutrals.200"
              borderRadius="8px"
              px="8px"
              py="4px"
              _hover={{ bg: 'neutrals.100' }}
            >
              Today
            </Box>
            <Button
              onClick={() => changeMonth(1)}
              aria-label="Next month"
              variant="ghost"
              p="0"
              _hover={{ bg: 'transparent' }}
            >
              <CaretRight size={32} />
            </Button>
          </Flex>
        </Flex>
        <Stack gap="24px" w="100%" overflowY="auto" h="calc(100% - 56px)">
          {isLoading ? (
            <LoadingSkeleton count={4} />
          ) : (
            <Stack w="100%" h="100%">
              <Calendar
                value={selectedDate}
                activeStartDate={selectedDate}
                onChange={handleDateChange}
                tileContent={tileContent}
                className="custom-calendar"
                calendarType="gregory"
                minDetail="month"
                aria-label="Interview calendar"
                showNavigation={false}
              />

              {selectedUpcomingInterview.length > 0 ? (
                <UpcomingInterviewsList
                  title={format(selectedDate, 'MMMM d, yyyy')}
                  interviews={selectedUpcomingInterview}
                  onDeleteSuccess={onDeleteSuccess}
                  onEditSuccess={onMeetingEdit}
                  agencyPlan={agencyPlan}
                />
              ) : (
                <NoMeetingsMessage title={format(selectedDate, 'MMMM d, yyyy')} />
              )}
            </Stack>
          )}
        </Stack>
      </Flex>
    </Box>
  );
};

interface RecentInterviewsSectionProps {
  recentInterviews: DateInterview[];
  isLoading: boolean;
  isLoadingMore: boolean;
  onLoadMore: () => void;
  hasMore: boolean;
}

const RecentInterviewsSection: React.FC<RecentInterviewsSectionProps> = ({
  recentInterviews,
  isLoading,
  isLoadingMore,
  onLoadMore,
  hasMore
}) => {
  const navigate = useNavigate();
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const handleScroll = () => {
      const { scrollTop, scrollHeight, clientHeight } = container;
      if (scrollHeight - scrollTop <= clientHeight * 1.5 && !isLoadingMore && hasMore) {
        onLoadMore();
      }
    };

    container.addEventListener('scroll', handleScroll);
    return () => container.removeEventListener('scroll', handleScroll);
  }, [isLoadingMore, hasMore, onLoadMore]);

  return (
    <Box
      flex={2}
      h="calc(100vh - 200px)"
      w="100%"
      p="32px"
      bg="background.secondary"
      borderRadius="12px"
      boxShadow="sm"
      _hover={{ boxShadow: 'md' }}
      transition="all 0.2s ease-in-out"
    >
      <Flex direction="column" align="flex-start" h="100%">
        <Text fontSize="24px" fontWeight="bold" mb="12px">
          Recent Sessions
        </Text>
        <Stack ref={containerRef} gap="32px" w="100%" overflowY="auto" h="calc(100% - 56px)">
          {isLoading && recentInterviews.length === 0 ? (
            <LoadingSkeleton count={4} />
          ) : recentInterviews.length > 0 ? (
            <>
              {recentInterviews.map((interview) => (
                <Stack key={interview.date.toISOString()}>
                  <Text fontSize="18px" fontWeight="bold" color="neutrals.800" mb="24px">
                    {format(interview.date, 'EEEE, MMMM d, yyyy')}
                  </Text>
                  <Stack gap="24px">
                    {interview.meetings.map((meeting) => (
                      <RecentInterviewCard
                        key={meeting.meeting_id}
                        meeting={meeting}
                        onClick={() => navigate(`/interview/${meeting.meeting_id}`)}
                      />
                    ))}
                  </Stack>
                </Stack>
              ))}
              {isLoadingMore && <LoadingSkeleton count={2} />}
            </>
          ) : (
            <EmptyState />
          )}
        </Stack>
      </Flex>
    </Box>
  );
};

const processMeetings = (meetings: MeetingSummary[]): DateInterview[] => {
  const meetingsMap: { [key: string]: DateInterview } = {};

  meetings.forEach((meeting) => {
    const meetingDate = new Date(meeting.meeting_start_time * 1000);
    const dateKey = meetingDate.toDateString();

    if (!meetingsMap[dateKey]) {
      meetingsMap[dateKey] = {
        date: meetingDate,
        meetings: []
      };
    }

    meetingsMap[dateKey].meetings.push(meeting);
  });

  return Object.values(meetingsMap);
};

export default function CopilotIndex() {
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [agencyPlan, setAgencyPlan] = useState(AgencyPlan.FREE);
  const [usage, setUsage] = useState<AgencyUsage | null>(null);
  const [recentInterviews, setRecentInterviews] = useState<DateInterview[]>([]);
  const [upcomingInterviews, setUpcomingInterviews] = useState<DateInterview[]>([]);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const PAGE_SIZE = 30;

  const fetchMeetings = useCallback(async () => {
    setIsLoading(true);
    setError(null);

    try {
      const response = await fetchAPI<GetMeetingDashboardResponse>({
        request: () => apiGetMeetingDashboard()
      });

      if (response.success) {
        const { upcoming_meetings, recent_meetings } = response.data;
        const processedRecentInterviews = processMeetings(recent_meetings);
        const processedUpcomingInterviews = processMeetings(upcoming_meetings);

        setRecentInterviews(processedRecentInterviews);
        setUpcomingInterviews(processedUpcomingInterviews);

        if (processedUpcomingInterviews.length > 0) {
          processedUpcomingInterviews.sort((a, b) => a.date.getTime() - b.date.getTime());
        }
      } else {
        setError('Failed to fetch meetings');
      }
    } catch (error) {
      console.error('Error fetching meetings:', error);
      setError('An error occurred while fetching meetings');
    }

    setIsLoading(false);
  }, []);

  const handleMeetingEdit = (meeting: MeetingSummary) => {
    const updatedInterviews = upcomingInterviews
      .map((i) => {
        const updatedMeetings = i.meetings.filter((m) => m.meeting_id !== meeting.meeting_id);
        return { ...i, meetings: updatedMeetings };
      })
      .filter((i) => i.meetings.length > 0);

    const meetingDate = new Date(meeting.meeting_start_time * 1000);
    const dateKey = meetingDate.toDateString();

    const existingDateInterview = updatedInterviews.find(
      (interview) => interview.date.toDateString() === dateKey
    );

    if (existingDateInterview) {
      existingDateInterview.meetings.push(meeting);
    } else {
      updatedInterviews.push({
        date: meetingDate,
        meetings: [meeting]
      });
    }

    updatedInterviews.sort((a, b) => a.date.getTime() - b.date.getTime());
    setUpcomingInterviews(updatedInterviews);
  };

  const handleDeleteSuccess = (meetingId: string) => {
    const updatedInterviews = upcomingInterviews
      .map((i) => {
        const updatedMeetings = i.meetings.filter((m) => m.meeting_id !== meetingId);
        return { ...i, meetings: updatedMeetings };
      })
      .filter((i) => i.meetings.length > 0);
    setUpcomingInterviews(updatedInterviews);
  };

  const fetchRecentMeetings = async (pageNum: number) => {
    setIsLoadingMore(true);
    try {
      const response = await fetchAPI<MeetingSummary[]>({
        request: () => apiGetRecentMeetings({ page: pageNum, limit: PAGE_SIZE })
      });

      if (response.success) {
        if (response.data.length === 0) {
          setHasMore(false);
          return;
        }

        const newMeetings = processMeetings(response.data);
        setRecentInterviews((prev) => [...prev, ...newMeetings]);
        setHasMore(newMeetings.length === PAGE_SIZE);
      } else {
        setError('Failed to fetch recent meetings');
      }
    } catch (error) {
      console.error('Error fetching recent meetings:', error);
      setError('An error occurred while fetching recent meetings');
    } finally {
      setIsLoadingMore(false);
    }
  };

  const handleLoadMore = () => {
    if (!isLoadingMore && hasMore) {
      setPage((prev) => prev + 1);
    }
  };

  useEffect(() => {
    fetchMeetings();
    fetchAccount();
  }, [fetchMeetings]);

  const fetchAccount = async () => {
    try {
      const response = await fetchAPI<Account>({
        request: () => apiGetAccountProfile()
      });

      if (response.success) {
        const agency = response.data.agency;
        setAgencyPlan(agency.plan);
        setUsage(agency.usages.find((usage) => usage.type === AgencyUsageType.MeetingTime) || null);
      } else {
        console.error('Failed to fetch account:', response.message);
        setError(`Failed to fetch account: ${response.message}`);
      }
    } catch (error) {
      console.error('Error fetching account:', error);
      setError(`An error occurred while fetching account: ${error}`);
    }
  };

  useEffect(() => {
    if (page > 1) {
      fetchRecentMeetings(page);
    }
  }, [page]);

  if (error) {
    return (
      <Flex w="100%" h="100%" align="center" justify="center" role="alert">
        <Text color="error.500">{error}</Text>
      </Flex>
    );
  }

  return (
    <Flex w="100%" justify="flex-start" align="flex-start" gap="24px" h="100%">
      <Box flex={2}>
        <RecentInterviewsSection
          recentInterviews={recentInterviews}
          isLoading={isLoading}
          isLoadingMore={isLoadingMore}
          onLoadMore={handleLoadMore}
          hasMore={hasMore}
        />
      </Box>
      <Stack flex={1} h="calc(100vh - 200px)">
        <CalendarSection
          upcomingInterviews={upcomingInterviews}
          onMeetingEdit={handleMeetingEdit}
          onDeleteSuccess={handleDeleteSuccess}
          isLoading={isLoading}
          agencyPlan={agencyPlan}
        />
        {!isLoading && agencyPlan === AgencyPlan.TRIAL && usage && (
          <Box
            w="100%"
            bg="background.secondary"
            border="1px solid"
            borderColor="neutrals.200"
            borderRadius="12px"
            p="16px 24px"
            boxShadow="sm"
            _hover={{ boxShadow: 'md' }}
            transition="all 0.2s ease-in-out"
          >
            <Stack gap={2}>
              <Flex justify="space-between" align="center">
                <Text fontSize="14px" color="neutrals.700" fontWeight="semibold">
                  Trial Usage
                </Text>
                <Text fontSize="14px" color={usage.exhausted ? 'error.500' : 'neutrals.700'}>
                  {usage.usage} / {MAX_MEETING_TIME_TOTAL} mins
                </Text>
              </Flex>
              <Box w="100%" h="8px" bg="neutrals.100" borderRadius="full" overflow="hidden">
                <Box
                  h="100%"
                  bg={usage.exhausted ? 'error.500' : 'primary.500'}
                  borderRadius="full"
                  transition="width 0.3s ease"
                  width={`${(usage.usage / MAX_MEETING_TIME_TOTAL) * 100}%`}
                />
              </Box>
            </Stack>
          </Box>
        )}
        {!isLoading && agencyPlan === AgencyPlan.FREE && (
          <Box
            w="100%"
            bg="background.secondary"
            border="1px solid"
            borderColor="error.500"
            borderRadius="12px"
            p="16px 24px"
          >
            <Stack gap={2}>
              <Text fontSize="14px" color="neutrals.700" fontWeight="semibold">
                Free Plan
              </Text>
              <Text fontSize="14px" color="neutrals.700">
                Upgrade to a paid plan to access the interview feature and unlock unlimited
                interviews. Contact us for the trial.
              </Text>
            </Stack>
          </Box>
        )}
      </Stack>
    </Flex>
  );
}
